import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useCreateCustomTest } from "@/hooks/agent/useBrandIntegrity";
import { CustomTestCategory } from "@/types/agent/brand-integrity";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CreateCustomTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (test: CustomTestCategory) => void;
}

export const CreateCustomTestModal = ({
  isOpen,
  onClose,
  onCreated,
}: CreateCustomTestModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [testPrompts, setTestPrompts] = useState<string[]>([""]);

  const { mutate: createTest, isPending } = useCreateCustomTest();

  const handleAddPrompt = () => {
    setTestPrompts([...testPrompts, ""]);
  };

  const handleRemovePrompt = (index: number) => {
    if (testPrompts.length > 1) {
      setTestPrompts(testPrompts.filter((_, i) => i !== index));
    }
  };

  const handlePromptChange = (index: number, value: string) => {
    const updated = [...testPrompts];
    updated[index] = value;
    setTestPrompts(updated);
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Please enter a test name");
      return;
    }

    const validPrompts = testPrompts.filter((p) => p.trim());
    if (validPrompts.length === 0) {
      toast.error("Please add at least one test prompt");
      return;
    }

    createTest(
      {
        name: name.trim(),
        description: description.trim(),
        testPrompts: validPrompts,
      },
      {
        onSuccess: (newTest) => {
          toast.success("Custom test created successfully");
          onCreated?.(newTest);
          handleClose();
        },
        onError: (error) => {
          console.error("Error creating custom test:", error);
          toast.error("Failed to create custom test");
        },
      }
    );
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setTestPrompts([""]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Create Custom Test</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Test Name */}
          <div className="space-y-2">
            <Label htmlFor="test-name" className="text-sm font-medium">
              Test Name
            </Label>
            <Input
              id="test-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Return Policy Questions"
              className="w-full"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe what this test evaluates..."
              className="w-full resize-none"
              rows={2}
            />
          </div>

          {/* Test Prompts */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Test Prompts</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-xs">
                      These are the messages a simulated user will send to your genie.
                      Add multiple prompts to test different variations of the scenario.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-3">
              {testPrompts.map((prompt, index) => (
                <div key={index} className="flex gap-2">
                  <Textarea
                    value={prompt}
                    onChange={(e) => handlePromptChange(index, e.target.value)}
                    placeholder={`e.g., "What's your return policy?" or "I want to return an item I bought last week"`}
                    className="flex-1 resize-none"
                    rows={2}
                  />
                  {testPrompts.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemovePrompt(index)}
                      className="h-10 w-10 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleAddPrompt}
              className="text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Another Prompt
            </Button>
          </div>

          {/* Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              Your custom test will simulate a conversation where the user sends these prompts to your genie.
              The test will evaluate how well your genie handles the scenario.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !name.trim()}
            className="bg-genie-primary hover:bg-genie-primary-hover text-white"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Test"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
